import { useCallback, useEffect, useRef, useState } from 'react'
import { LAYI_BASE_PATH, loginToLayi } from './layiAuth'
import { areLayiNotificationsOn, refreshLayiNotificationsText, showLayiNotification } from './layiNotifications'
import { createRecoveryBudget } from './recoveryBudget'

// Session for the embedded LAYI (the native Chainlit UI in an iframe, same-origin under /layi):
// log in (POST /layi/auth/header → the access_token cookie the iframe rides on), then show the
// frame, and heal the session when it is lost.
//
// Loss shows up as the frame landing on Chainlit's login page. Chainlit is an SPA: it gets there by
// a client-side redirect (no `load` event on the parent side), so the frame's location is polled.
// Same origin makes `contentWindow.location` readable; anything that makes it unreadable (a
// cross-origin LAYI some day) simply disables the self-heal — the user still has the retry button.
//
// The frame's `key` is the recovery epoch: a bumped epoch after a fresh login remounts it, so the
// new cookie is used from the first request.
//
// It also turns LAYI's "answer finished" signal into a browser notification (layiNotifications.js).

export const LAYI_FRAME_SRC = `${LAYI_BASE_PATH}/`
export const LAYI_LOGIN_PATH = `${LAYI_BASE_PATH}/login`
export const LAYI_LOCATION_POLL_MS = 1000
// Automatic self-heals between manual retries (see recoveryBudget.js for the healthy-window rule).
export const LAYI_RECOVERY_MAX_ATTEMPTS = 2
export const LAYI_RECOVERY_BUDGET_RESET_AFTER_MS = 5 * 60 * 1000
// The window message LAYI's server sends when an answer finishes (no content besides the thread id).
export const LAYI_TURN_FINISHED = 'layi:turn-finished'

export const FRAME_STATUS = { LOGIN: 'login', READY: 'ready', ERROR: 'error' }

// Failures travel as translation keys (like layiAuth's reasons): the UI renders them with `t`.
const RECOVERY_EXHAUSTED_REASON = 'layi_error_login_failed'

// The path of the LAYI document inside the frame, or null when there is nothing to judge: no frame
// yet, `about:blank` before the first load, or a location this origin may not read.
export const readFramePathname = frame => {
  try {
    const pathname = frame && frame.contentWindow && frame.contentWindow.location.pathname
    return typeof pathname === 'string' && pathname.startsWith(LAYI_BASE_PATH) ? pathname : null
  } catch (error) {
    return null
  }
}

const loginStatus = { kind: FRAME_STATUS.LOGIN, messageKey: null }
const readyStatus = { kind: FRAME_STATUS.READY, messageKey: null }

export default function useLayiFrame({ locale, notificationTitle, notificationBody }) {
  const [status, setStatus] = useState(loginStatus)
  const [epoch, setEpoch] = useState(0)
  const frameRef = useRef(null)
  const budgetRef = useRef(null)
  if (budgetRef.current === null) {
    budgetRef.current = createRecoveryBudget({ maxAttempts: LAYI_RECOVERY_MAX_ATTEMPTS, resetAfterMs: LAYI_RECOVERY_BUDGET_RESET_AFTER_MS })
  }

  // A locale change is a new identity for the budget (the mobile app does the same).
  useEffect(() => {
    budgetRef.current.reset()
  }, [locale])

  // Log in on mount, on every recovery epoch and when the locale changes (the login stores the
  // locale in the LAYI user metadata, so the pop-out-style UI answers in the app's language).
  // Nothing here says "embedded": LAYI's page tells a frame from its own window (`window.top`)
  // and hides its user menu and theme switch inside one by itself.
  useEffect(() => {
    let cancelled = false
    setStatus(loginStatus)
    loginToLayi({ locale }).then(result => {
      if (cancelled) return
      setStatus(result.ok ? readyStatus : { kind: FRAME_STATUS.ERROR, messageKey: result.reason })
    })
    return () => {
      cancelled = true
    }
  }, [locale, epoch])

  // While the frame is up, watch where its document is.
  useEffect(() => {
    if (status.kind !== FRAME_STATUS.READY) return undefined
    const timer = setInterval(() => {
      const pathname = readFramePathname(frameRef.current)
      if (pathname === null) return
      if (pathname !== LAYI_LOGIN_PATH) {
        budgetRef.current.markHealthy()
        return
      }
      if (budgetRef.current.tryConsume()) setEpoch(previous => previous + 1)
      else setStatus({ kind: FRAME_STATUS.ERROR, messageKey: RECOVERY_EXHAUSTED_REASON })
    }, LAYI_LOCATION_POLL_MS)
    return () => clearInterval(timer)
  }, [status.kind])

  // While LAYI notifications are on, keep their stored text in the app's language (LAYI's own pages
  // show it). This also reads the permission from the start, which a hidden Safari tab needs: WebKit
  // suspends it after a few minutes unless the page has read the permission as granted.
  useEffect(() => {
    refreshLayiNotificationsText({ title: notificationTitle, body: notificationBody })
  }, [notificationTitle, notificationBody])

  // Chainlit posts LAYI's window messages to window.parent: this page while the chat is framed. Only
  // this hook's frame counts, read when the message arrives (there is none while logging in or
  // failed, and each recovery epoch brings a new one), so a signal has one receiver even with two
  // frames in the tab. Looking at the chat means a visible, focused tab with the frame on screen: a
  // collapsed panel hides it with display: none, which leaves it without boxes.
  useEffect(() => {
    const onMessage = event => {
      const frame = frameRef.current
      if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin) return
      if (!event.data || typeof event.data !== 'object' || event.data.type !== LAYI_TURN_FINISHED) return
      if (!areLayiNotificationsOn()) return
      const looking = document.visibilityState === 'visible' && document.hasFocus() && frame.getClientRects().length > 0
      if (!looking) showLayiNotification({ title: notificationTitle, body: notificationBody })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [notificationTitle, notificationBody])

  const retry = useCallback(() => {
    budgetRef.current.reset()
    setEpoch(previous => previous + 1)
  }, [])

  return { status, epoch, frameRef, retry }
}
